/**
 * @file BrandSection.tsx
 * @description «Настройки сайта → Бренд» (phase19.16 Р1; SB18, SB71, SB77;
 * uiux-design-r1 §4): название на сайте, слоган, логотипы для светлого и
 * тёмного фона (PNG/WebP), значок вкладки и как он выглядит во вкладке.
 * На широкой панели (§7.16 фазы): название | слоган в ряд, логотипы и
 * значок — в ряд, образец вкладки — справа от полей.
 */

import { FieldRow, FieldsWithPreview } from "../FormLayout";
import { MediaField, LOGO_MIME } from "../media/MediaField";
import { TextField } from "../TextField";
import type { PartFieldsApi } from "../../../hooks/site-view/usePartFields";
import type { SiteBrandDraft } from "../../../lib/site-view/types";

const LOGO_HINT = "PNG или WebP с прозрачным фоном, высотой от 160px. SVG пока не принимаем.";

/** Образец вкладки браузера со значком и названием. */
function TabSample({ brand }: { brand: SiteBrandDraft }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="type-caption text-on-surface-variant">Так во вкладке браузера</span>
      <div className="flex max-w-80 items-center gap-2 rounded-t-lg bg-surface-low px-3 py-2">
        {brand.favicon ? <img src={brand.favicon.url} alt="" className="size-4 object-contain" /> : <span className="size-4 rounded-sm bg-on-surface/20" />}
        <span className="type-caption truncate text-on-surface">Каталог — {brand.siteName || "Магазин"}</span>
        <span aria-hidden className="ml-auto text-on-surface-variant">×</span>
      </div>
    </div>
  );
}

export function BrandSection({ brand }: { brand: PartFieldsApi<SiteBrandDraft> }) {
  const { fields, set } = brand;
  const nameEmpty = fields.siteName.trim() === "";
  return (
    <FieldsWithPreview preview={<TabSample brand={fields} />}>
      <FieldRow>
        <TextField
          label="Название на сайте"
          value={fields.siteName}
          max={40}
          hint="В шапке, во вкладке браузера и в письмах. Это не юридическое наименование."
          error={nameEmpty ? "Название нужно: без него в шапке и во вкладке будет пусто" : null}
          onChange={(v) => {
            // Пустое название сервер не примет: храним его только в поле, пока не введут.
            if (v.trim() === "") brand.change({ ...fields, siteName: v }, []);
            else set("siteName", v);
          }}
        />
        <TextField label="Слоган" value={fields.tagline} max={60} onChange={(v) => set("tagline", v)} />
      </FieldRow>
      <FieldRow cols={3}>
        <MediaField
          label="Логотип для светлого фона"
          value={fields.logoLight}
          accept={LOGO_MIME}
          hint={fields.logoLight ? LOGO_HINT : `Логотип не загружен — в шапке будет название текстом. ${LOGO_HINT}`}
          onChange={(m) => set("logoLight", m)}
        />
        <MediaField
          label="Логотип для тёмного фона"
          value={fields.logoDark}
          accept={LOGO_MIME}
          dark
          hint="Для тёмной шапки и подвала."
          onChange={(m) => set("logoDark", m)}
        />
        <MediaField
          label="Значок вкладки"
          value={fields.favicon}
          accept={LOGO_MIME}
          shape="square"
          hint="Квадрат от 512px, PNG или WebP."
          onChange={(m) => set("favicon", m)}
        />
      </FieldRow>
    </FieldsWithPreview>
  );
}
